import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronRight, ListChecks } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  type Article,
  ARTICLE_KIND_LABELS,
  formatEuro,
  listArticles,
  listCategories,
  listTauxTva,
} from '@/modules/catalogue/api';
import { ChoixChantier } from '@/modules/chantiers/ChoixChantier';
import { listChantiers } from '@/modules/chantiers/api';
import { ChoixClient } from '@/modules/clients/ChoixClient';
import { listContacts } from '@/modules/clients/api';
import {
  computeDevisTotals,
  createDevis,
  type FormLigne,
  type LigneInsert,
  nextDevisNumber,
} from '@/modules/devis/api';

const NONE = '__sans__';

function num(value: string): number {
  const n = Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function isoDay(offset = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export function DevisCataloguePage() {
  const navigate = useNavigate();

  const articlesQ = useQuery({ queryKey: ['articles'], queryFn: listArticles });
  const categoriesQ = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  const tvaQ = useQuery({ queryKey: ['taux-tva'], queryFn: listTauxTva });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });

  const [contactId, setContactId] = useState('');
  const [chantierId, setChantierId] = useState('');
  const [objet, setObjet] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [qtyById, setQtyById] = useState<Record<string, string>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const articles = useMemo(
    () => (articlesQ.data ?? []).filter((a) => a.actif),
    [articlesQ.data],
  );
  const articlesById = useMemo(() => new Map(articles.map((a) => [a.id, a])), [articles]);
  const cats = categoriesQ.data ?? [];
  const catOrder = useMemo(() => {
    const m = new Map<string, number>();
    cats.forEach((c, i) => m.set(c.id, i));
    m.set(NONE, cats.length);
    return m;
  }, [cats]);
  const catName = useMemo(() => {
    const m = new Map<string, string>(cats.map((c) => [c.id, c.nom]));
    m.set(NONE, 'Sans catégorie');
    return m;
  }, [cats]);
  const tvaList = tvaQ.data ?? [];

  const searching = query.trim() !== '';

  // Articles visibles (recherche + type), groupés par catégorie.
  const groups = useMemo(() => {
    const visible = filtrerRecherche(
      articles.filter((a) => kind === '' || a.kind === kind),
      query,
      (a) => [a.libelle, a.reference],
    );
    const g = new Map<string, Article[]>();
    for (const a of visible) {
      const k = a.categorie_id ?? NONE;
      const arr = g.get(k) ?? [];
      arr.push(a);
      g.set(k, arr);
    }
    for (const arr of g.values()) arr.sort((a, b) => a.libelle.localeCompare(b.libelle));
    return [...g.entries()].sort(
      (a, b) => (catOrder.get(a[0]) ?? 999) - (catOrder.get(b[0]) ?? 999),
    );
  }, [articles, kind, query, catOrder]);

  // Lignes sélectionnées (depuis TOUT le catalogue, même masquées par un filtre).
  const selected = useMemo(() => {
    const items = Object.keys(qtyById)
      .map((id) => articlesById.get(id))
      .filter((a): a is Article => Boolean(a));
    items.sort((a, b) => {
      const ca = catOrder.get(a.categorie_id ?? NONE) ?? 999;
      const cb = catOrder.get(b.categorie_id ?? NONE) ?? 999;
      return ca - cb || a.libelle.localeCompare(b.libelle);
    });
    return items;
  }, [qtyById, articlesById, catOrder]);

  const formLignes: FormLigne[] = selected.map((a) => ({
    article_id: a.id,
    libelle: a.libelle,
    description: a.description ?? '',
    quantite: qtyById[a.id] ?? '1',
    unite_id: a.unite_id,
    prix_unitaire_ht: a.prix_vente_ht,
    remise_pourcentage: '0',
    taux_tva_id: a.taux_tva_id,
  }));
  const totals = computeDevisTotals(formLignes, tvaList);

  const selectedByCat = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of selected) {
      const k = a.categorie_id ?? NONE;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [selected]);

  const toggle = (a: Article) => {
    setQtyById((prev) => {
      const next = { ...prev };
      if (next[a.id] !== undefined) delete next[a.id];
      else next[a.id] = '1';
      return next;
    });
  };
  const setQty = (id: string, value: string) => {
    setQtyById((prev) => ({ ...prev, [id]: value }));
  };
  const toggleCat = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const lignes: LigneInsert[] = formLignes.map((l, i) => ({
        ordre: i,
        type: 'article',
        article_id: l.article_id === '' ? null : l.article_id,
        libelle: l.libelle,
        description: l.description.trim() === '' ? null : l.description.trim(),
        quantite: String(num(l.quantite) || 0),
        unite_id: l.unite_id === '' ? null : l.unite_id,
        prix_unitaire_ht: String(num(l.prix_unitaire_ht) || 0),
        remise_pourcentage: '0',
        taux_tva_id: l.taux_tva_id === '' ? null : l.taux_tva_id,
        total_ht: (totals.ligneTotals[i]?.total_ht ?? 0).toFixed(2),
        total_ttc: (totals.ligneTotals[i]?.total_ttc ?? 0).toFixed(2),
      }));
      const header = {
        contact_id: contactId,
        chantier_id: chantierId === '' ? null : chantierId,
        objet: objet.trim() === '' ? 'Devis' : objet.trim(),
        date_emission: isoDay(0),
        date_expiration: isoDay(30),
        statut: 'brouillon',
        montant_ht: totals.montant_ht.toFixed(2),
        montant_tva: totals.montant_tva.toFixed(2),
        montant_ttc: totals.montant_ttc.toFixed(2),
        detail_tva: totals.detail_tva,
      };
      const numero = await nextDevisNumber();
      return createDevis(header, lignes, numero);
    },
    onSuccess: (devisId) => {
      navigate(`/devis/${devisId}`);
    },
  });

  const canCreate =
    contactId !== '' && chantierId !== '' && selected.length > 0 && !createMut.isPending;
  const loading = articlesQ.isLoading || categoriesQ.isLoading;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="icon">
          <Link to="/devis" aria-label="Retour">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Devis express</h1>
          <p className="text-muted-foreground">
            Cochez les prestations dans votre catalogue, indiquez les quantités : le devis se monte
            tout seul.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Client</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="contact_id">Client *</Label>
            <ChoixClient
              id="contact_id"
              clients={contactsQ.data ?? []}
              chargement={contactsQ.isLoading}
              value={contactId}
              onChange={setContactId}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chantier_id">Chantier *</Label>
            <ChoixChantier
              id="chantier_id"
              chantiers={chantiersQ.data ?? []}
              clients={contactsQ.data ?? []}
              chargement={chantiersQ.isLoading}
              value={chantierId}
              onChange={setChantierId}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="objet">Objet du devis</Label>
            <Input
              id="objet"
              value={objet}
              placeholder="Ex : Réfection toiture"
              onChange={(e) => {
                setObjet(e.target.value);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prestations</CardTitle>
          <div className="flex flex-col gap-3 pt-2 sm:flex-row">
            <BarreRecherche
              valeur={query}
              onChange={setQuery}
              quoi="une prestation"
              champs="désignation, référence"
              className="flex-1"
            />
            <Select
              value={kind}
              className="sm:w-56"
              onChange={(e) => {
                setKind(e.target.value);
              }}
            >
              <option value="">Tous les types</option>
              {Object.entries(ARTICLE_KIND_LABELS).map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <p className="text-muted-foreground">Chargement du catalogue…</p>
          ) : groups.length === 0 ? (
            <p className="text-muted-foreground">Aucune prestation ne correspond.</p>
          ) : (
            groups.map(([key, list]) => {
              const open = searching || !collapsed.has(key);
              const nSel = selectedByCat.get(key) ?? 0;
              return (
                <div key={key} className="rounded-md border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium hover:bg-muted/40"
                    onClick={() => {
                      if (!searching) toggleCat(key);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {open ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      {catName.get(key) ?? 'Catégorie'}
                      <span className="text-xs text-muted-foreground">({list.length})</span>
                    </span>
                    {nSel > 0 ? (
                      <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {nSel} sélectionnée{nSel > 1 ? 's' : ''}
                      </span>
                    ) : null}
                  </button>
                  {open ? (
                    <ul className="divide-y border-t">
                      {list.map((a) => {
                        const checked = qtyById[a.id] !== undefined;
                        return (
                          <li
                            key={a.id}
                            className={`flex flex-wrap items-center gap-3 px-3 py-2 ${checked ? 'bg-primary/5' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 shrink-0 cursor-pointer accent-primary"
                              checked={checked}
                              onChange={() => {
                                toggle(a);
                              }}
                              aria-label={a.libelle}
                            />
                            <button
                              type="button"
                              className="flex-1 text-left text-sm"
                              onClick={() => {
                                toggle(a);
                              }}
                            >
                              {a.libelle}
                              <span className="ml-2 text-xs text-muted-foreground">
                                {ARTICLE_KIND_LABELS[a.kind] ?? a.kind}
                              </span>
                            </button>
                            <span className="w-24 text-right text-sm tabular-nums text-muted-foreground">
                              {formatEuro(a.prix_vente_ht)}
                            </span>
                            {checked ? (
                              <Input
                                inputMode="decimal"
                                value={qtyById[a.id] ?? '1'}
                                className="w-24"
                                aria-label={`Quantité ${a.libelle}`}
                                onChange={(e) => {
                                  setQty(a.id, e.target.value);
                                }}
                              />
                            ) : (
                              <span className="w-20 text-right text-xs text-muted-foreground">
                                —
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {createMut.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          Échec : {messageErreur(createMut.error)}
        </div>
      ) : null}

      {/* Barre récapitulative fixe */}
      <div className="sticky bottom-0 z-10 -mx-4 -mb-4 border-t bg-card sm:-mx-6 sm:-mb-6 lg:-mx-8 lg:-mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="text-sm">
            <span className="font-medium">{selected.length}</span> ligne
            {selected.length > 1 ? 's' : ''} ·{' '}
            <span className="text-muted-foreground">Total estimé</span>{' '}
            <span className="font-semibold">{formatEuro(totals.montant_ttc)}</span>{' '}
            <span className="text-xs text-muted-foreground">TTC ({formatEuro(totals.montant_ht)} HT)</span>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" type="button">
              <Link to="/devis">Annuler</Link>
            </Button>
            <Button
              type="button"
              disabled={!canCreate}
              onClick={() => {
                createMut.mutate();
              }}
            >
              <ListChecks className="h-4 w-4" />
              {createMut.isPending ? 'Création…' : 'Créer le devis'}
            </Button>
          </div>
        </div>
        {contactId === '' && selected.length > 0 ? (
          <p className="px-4 pb-2 text-center text-xs text-muted-foreground sm:px-6">
            Sélectionnez un client pour créer le devis.
          </p>
        ) : null}
      </div>
    </div>
  );
}
